import * as THREE from 'three'

export type NecklaceStyle = 'pendant' | 'mangalsutra' | 'choker' | 'pearls'

export interface ProceduralNecklaceOptions {
  style: NecklaceStyle
  metal: number | string
  accent: number | string
  /** Drop of the front (lowest point) below the notch, cm. */
  drop: number
  /** Neck ring radius (cm). */
  neckRadius: number
}

export const NECKLACE_DEFAULTS: ProceduralNecklaceOptions = {
  style: 'pendant',
  metal: 0xd4a94a,
  accent: 0xf6f1e7,
  drop: 7,
  neckRadius: 6.4,
}

function metal(c: number | string): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color: new THREE.Color(c), metalness: 1, roughness: 0.28, envMapIntensity: 1.1 })
}
function pearl(c: number | string): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({ color: new THREE.Color(c), metalness: 0, roughness: 0.35, clearcoat: 0.9, clearcoatRoughness: 0.25, envMapIntensity: 0.7 })
}
function stone(c: number | string): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({ color: new THREE.Color(c), metalness: 0, roughness: 0.15, clearcoat: 1, envMapIntensity: 0.9 })
}

/**
 * Chain curve: a ring around the neck (radius r, centre slightly behind the notch) whose front
 * sags down the chest by `drop`. Parameter a=0 at the back, a=π at the front (lowest point).
 * Frame: origin at the suprasternal notch, +Y up, +Z out of the chest, +X subject's left.
 */
export function chainCurve(r: number, drop: number, chestZ = 2.0): THREE.Curve<THREE.Vector3> {
  return new (class extends THREE.Curve<THREE.Vector3> {
    constructor() {
      super()
    }
    override getPoint(t: number, target = new THREE.Vector3()): THREE.Vector3 {
      const a = t * Math.PI * 2
      const front = (1 - Math.cos(a)) / 2 // 0 back → 1 front
      const x = Math.sin(a) * r
      const z = -Math.cos(a) * r - 1.5 + front * chestZ
      const y = 1.2 - front * (drop + 1.2) // back of the neck sits a bit above the notch
      return target.set(x, y, z)
    }
  })()
}

export function createProceduralNecklace(options: Partial<ProceduralNecklaceOptions> = {}): THREE.Group {
  const o = { ...NECKLACE_DEFAULTS, ...options }
  const g = new THREE.Group()
  g.name = `procedural-necklace-${o.style}`
  const gold = metal(o.metal)

  switch (o.style) {
    case 'choker': {
      const curve = chainCurve(o.neckRadius + 0.2, 1.2, 0.4)
      const band = new THREE.Mesh(new THREE.TubeGeometry(curve, 96, 0.35, 10, true), pearl(o.accent))
      band.name = 'band'
      const gem = new THREE.Mesh(new THREE.SphereGeometry(0.55, 16, 12), stone(o.metal))
      gem.name = 'gem'
      gem.position.copy(curve.getPoint(0.5)).add(new THREE.Vector3(0, 0, 0.3))
      g.add(band, gem)
      break
    }
    case 'pearls': {
      const curve = chainCurve(o.neckRadius, o.drop)
      const n = 56
      const bead = new THREE.InstancedMesh(new THREE.SphereGeometry(0.42, 14, 10), pearl(o.accent), n)
      bead.name = 'pearls'
      const m = new THREE.Matrix4()
      for (let i = 0; i < n; i++) {
        m.setPosition(curve.getPoint(i / n))
        bead.setMatrixAt(i, m)
      }
      g.add(bead)
      break
    }
    case 'mangalsutra': {
      const curve = chainCurve(o.neckRadius, o.drop)
      const n = 110
      const blackBeads = new THREE.InstancedMesh(new THREE.SphereGeometry(0.17, 10, 8), pearl(0x111111), n)
      blackBeads.name = 'black_beads'
      const goldBeads = new THREE.InstancedMesh(new THREE.SphereGeometry(0.2, 10, 8), gold, Math.ceil(n / 6))
      goldBeads.name = 'gold_beads'
      const m = new THREE.Matrix4()
      let gi = 0
      for (let i = 0; i < n; i++) {
        m.setPosition(curve.getPoint(i / n))
        blackBeads.setMatrixAt(i, m)
        if (i % 6 === 0) goldBeads.setMatrixAt(gi++, m)
      }
      const front = curve.getPoint(0.5)
      const pendant = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.75, 1.4, 16), gold)
      pendant.name = 'pendant'
      pendant.position.set(front.x, front.y - 0.9, front.z + 0.2)
      const pendant2 = pendant.clone()
      pendant2.name = 'pendant2'
      pendant.position.x -= 0.75
      pendant2.position.x += 0.75
      g.add(blackBeads, goldBeads, pendant, pendant2)
      break
    }
    case 'pendant':
    default: {
      const curve = chainCurve(o.neckRadius, o.drop)
      const chain = new THREE.Mesh(new THREE.TubeGeometry(curve, 128, 0.08, 8, true), gold)
      chain.name = 'chain'
      const front = curve.getPoint(0.5)
      const bail = new THREE.Mesh(new THREE.TorusGeometry(0.28, 0.07, 8, 16), gold)
      bail.name = 'bail'
      bail.position.set(front.x, front.y - 0.2, front.z)
      const drop = new THREE.Mesh(new THREE.SphereGeometry(0.7, 22, 16), stone(o.accent))
      drop.name = 'pendant'
      drop.scale.set(0.8, 1.15, 0.5)
      drop.position.set(front.x, front.y - 1.2, front.z + 0.1)
      const rim = new THREE.Mesh(new THREE.TorusGeometry(0.72, 0.06, 8, 28), gold)
      rim.name = 'pendant_rim'
      rim.scale.set(0.8, 1.15, 0.6)
      rim.position.copy(drop.position)
      g.add(chain, bail, drop, rim)
      break
    }
  }
  return g
}
